import { Logo, MHidden, NavSection, Scrollbar } from '@bakerweb/components';
import menu2Fill from '@iconify/icons-eva/menu-2-fill';
import { Icon } from '@iconify/react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  AppBar,
  Box,
  Button,
  Chip,
  ClickAwayListener,
  Drawer,
  Grow,
  ListSubheader,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  Toolbar,
  Typography
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { AccountPopover, AccountPopoverAccount } from '../shared/AccountPopover';
import { ContactPopover } from '../shared/ContactPopover';
import { BakerNavGroup, BakerNavLink, groupLinks, useBakerNavConfig } from './BakerNavConfig';

const APPBAR_MOBILE = 56;
const APPBAR_DESKTOP = 64;
const DRAWER_WIDTH = 280;
const MORE_KEY = '__more__';

// Group buttons rendered inline on the bar before the rest spill into a
// trailing "More" dropdown. Keeps the menu bar from overflowing horizontally
// as new tool groups are added.
const MAX_INLINE_GROUPS = 6;

const RootStyle = styled(AppBar)(({ theme }) => ({
  boxShadow: 'none',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
  backgroundColor: alpha(theme.palette.background.default, 0.72),
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`
}));

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: APPBAR_MOBILE,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  [theme.breakpoints.up('lg')]: {
    minHeight: APPBAR_DESKTOP,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3)
  }
}));

const BetaChip = (
  <Chip label="Beta" size="small" color="warning" variant="outlined" sx={{ ml: 1, height: 20, fontSize: 11 }} />
);

/** True when the current path falls under any destination in the group. */
// Exact match or a full path segment below it — avoids a prefix like
// /dashboard/cash spuriously matching /dashboard/cash-flows.
function pathMatches(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

function isGroupActive(group: BakerNavGroup, pathname: string): boolean {
  // Check each item's own path AND its children, so a group whose parent item
  // has a real route (e.g. a bare /dashboard/loans) still activates.
  return group.items.some(
    (item) => pathMatches(pathname, item.path) || (item.children ?? []).some((child) => pathMatches(pathname, child.path))
  );
}

/**
 * Renders a flat list of links as MenuItems, inserting a ListSubheader whenever
 * the parent label changes (keeps e.g. Search Bonds' General/Muni grouped under
 * a "Search Bonds" heading). Links from the same parent are always contiguous.
 */
function renderLinks(links: BakerNavLink[], onNavigate: () => void) {
  const out: JSX.Element[] = [];
  let lastParent: string | undefined;
  links.forEach((link, i) => {
    if (link.parentLabel && link.parentLabel !== lastParent) {
      out.push(
        <ListSubheader key={`sub-${i}`} disableSticky sx={{ bgcolor: 'transparent', lineHeight: '32px' }}>
          {link.parentLabel}
        </ListSubheader>
      );
    }
    lastParent = link.parentLabel;
    out.push(
      <MenuItem
        key={link.path}
        component={RouterLink}
        to={link.path}
        onClick={onNavigate}
        sx={{ pl: link.parentLabel ? 3 : 2 }}
      >
        {link.icon && <Box sx={{ mr: 1.5, display: 'inline-flex', '& svg': { fontSize: 20 } }}>{link.icon}</Box>}
        {link.title}
      </MenuItem>
    );
  });
  return out;
}

type OpenState = { key: string; anchor: HTMLElement } | null;

type BakerTopNavProps = {
  /** Signed-in Entra account (email + sign-out) for the shared AccountPopover. */
  account?: AccountPopoverAccount;
};

export function BakerTopNav({ account }: BakerTopNavProps = {}) {
  const { pathname } = useLocation();
  const navConfig = useBakerNavConfig();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Which dropdown (group subheader or MORE_KEY) is open, and its anchor button.
  // Lifted here so hovering/clicking another button switches instantly — a modal
  // <Menu> would drop an invisible backdrop that swallows the next button's click.
  const [open, setOpen] = useState<OpenState>(null);

  // Close menus on route change (covers link clicks and the mobile drawer).
  useEffect(() => {
    setOpen(null);
    setMobileOpen(false);
  }, [pathname]);

  // Memoized so the derived slices are stable across renders (they feed
  // openContent's useMemo dependency array).
  const inlineGroups = useMemo(() => navConfig.slice(0, MAX_INLINE_GROUPS), [navConfig]);
  const overflowGroups = useMemo(() => navConfig.slice(MAX_INLINE_GROUPS), [navConfig]);

  // Click opens; clicking the open one closes; clicking another switches in a
  // single click. No hover-to-open — that made a follow-up click read as "close".
  const toggle = (key: string, el: HTMLElement) => setOpen((cur) => (cur?.key === key ? null : { key, anchor: el }));
  const close = () => setOpen(null);
  // The trigger buttons live outside the Popper, so a click on one would
  // normally fire click-away (close) AND the button's toggle (reopen). Skip
  // click-away for the buttons and let toggle be the single source of truth.
  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('[data-nav-button="true"]')) return;
    close();
  };

  const moreActive = overflowGroups.some((g) => isGroupActive(g, pathname));

  // Content for whichever dropdown is currently open.
  const openContent = useMemo(() => {
    if (!open) return null;
    if (open.key === MORE_KEY) {
      return overflowGroups.flatMap((group, gi) => [
        <ListSubheader
          key={`grp-${group.subheader}`}
          disableSticky
          sx={{
            bgcolor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            mt: gi === 0 ? 0 : 1,
            lineHeight: '28px',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            color: 'text.secondary'
          }}
        >
          {group.subheader}
          {group.beta && BetaChip}
        </ListSubheader>,
        ...renderLinks(groupLinks(group), close)
      ]);
    }
    const group = navConfig.find((g) => g.subheader === open.key);
    return group ? renderLinks(groupLinks(group), close) : null;
  }, [open, navConfig, overflowGroups]);

  return (
    <RootStyle position="fixed">
      <ToolbarStyle>
        <Box component={RouterLink} to="/dashboard" sx={{ display: 'inline-flex', alignItems: 'center', mr: 2 }}>
          <Logo sx={{ width: 96, height: 40 }} />
        </Box>

        {/* Desktop: grouped dropdown buttons + More overflow */}
        <MHidden width="lgDown">
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {inlineGroups.map((group) => {
              const active = isGroupActive(group, pathname);
              return (
                <Button
                  key={group.subheader}
                  data-nav-button="true"
                  onClick={(e) => toggle(group.subheader, e.currentTarget)}
                  endIcon={<KeyboardArrowDownIcon />}
                  color="inherit"
                  sx={{
                    px: 1.5,
                    fontWeight: active ? 'fontWeightBold' : 'fontWeightMedium',
                    color: active ? 'primary.main' : 'text.primary'
                  }}
                >
                  {group.subheader}
                  {group.beta && BetaChip}
                </Button>
              );
            })}
            {overflowGroups.length > 0 && (
              <Button
                data-nav-button="true"
                onClick={(e) => toggle(MORE_KEY, e.currentTarget)}
                endIcon={<KeyboardArrowDownIcon />}
                color="inherit"
                sx={{ px: 1.5, color: moreActive ? 'primary.main' : 'text.primary' }}
              >
                More
              </Button>
            )}
          </Stack>

          <Popper
            open={Boolean(open)}
            anchorEl={open?.anchor}
            placement="bottom-start"
            transition
            sx={{ zIndex: (theme) => theme.zIndex.appBar + 1 }}
          >
            {({ TransitionProps }) => (
              <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
                <Paper elevation={8} sx={{ mt: 0.5, minWidth: 220, maxHeight: '80vh', overflowY: 'auto' }}>
                  <ClickAwayListener onClickAway={handleClickAway}>
                    <MenuList dense>{openContent}</MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </MHidden>

        {/* Mobile / narrow: single "Menu" button → drawer with the full grouped nav */}
        <MHidden width="lgUp">
          <Button
            onClick={() => setMobileOpen(true)}
            startIcon={<Icon icon={menu2Fill} />}
            color="inherit"
            aria-label="Open navigation menu"
            sx={{ color: 'text.primary' }}
          >
            Menu
          </Button>
        </MHidden>

        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1.5 }}>
          <ContactPopover />
          <AccountPopover account={account} />
        </Stack>
      </ToolbarStyle>

      <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)} PaperProps={{ sx: { width: DRAWER_WIDTH } }}>
        <Scrollbar sx={{ height: '100%' }}>
          <Stack sx={{ px: 2.5, pt: 3, pb: 2 }}>
            <Box component={RouterLink} to="/dashboard" sx={{ display: 'inline-flex' }}>
              <Logo sx={{ width: 150, height: 70 }} />
            </Box>
            <Typography
              variant="h6"
              sx={{ mt: 1, color: 'primary.main', fontWeight: 600, fontSize: '1rem', letterSpacing: 0.5 }}
            >
              Baker Portal
            </Typography>
          </Stack>
          <NavSection navConfig={navConfig} />
        </Scrollbar>
      </Drawer>
    </RootStyle>
  );
}
